import { stationWebSocketClient } from './client';
import {
  muteNativeCompanion,
  sendCompanionMessage,
  sendPrintMessage,
  sendSearchResultsMessage,
  sendSelectCompanionMessage,
  sendSpeakMessage,
  sendStartHostMessage,
  sendStartOverMessage,
  sendStopTalkingMessage,
  updateNativeCompanionVolume,
} from './companionBridge';
import {
  startBusinessPlanGeneration,
  StartBusinessPlanGenerationParams,
  startImageGeneration,
  StartImageGenerationParams,
  startMusicGeneration,
  StartMusicGenerationParams,
  startPresentationGeneration,
  StartPresentationGenerationParams,
  startSpacesUseCase,
  StartSpacesUseCaseParams,
  startWebpageGeneration,
  StartWebpageGenerationParams,
} from './generationBridge';
import { shouldUseWebSocket } from './helpers';
import {
  duckAudio,
  playCreation,
  PlayCreationParams,
  PlayParams,
  playTrackOrAlbum,
} from './playbackBridge';
import {
  regenerateBusinessPlan,
  RegenerateBusinessPlanParams,
  regenerateCardImage,
  RegenerateCardImageParams,
  regenerateImage,
  RegenerateImageParams,
  regenerateLyrics,
  RegenerateLyricsParams,
  regeneratePresentation,
  RegeneratePresentationParams,
  regenerateWebpage,
  RegenerateWebpageParams,
} from './regenerationBridge';
import { performSearch, SearchParams } from './searchBridge';
import {
  goToNext,
  shareGeneration,
  switchToNextVariant,
} from './uiActionsBridge';

export const handleWebSocketMessage = (message: any) => {
  const { type, payload } = message;

  console.log('[Station WS Bridge] Handling message:', type, payload);

  switch (type) {
    case 'startMusicGeneration':
      if (payload) {
        startMusicGeneration(payload as StartMusicGenerationParams).catch(
          (error) => {
            console.error(
              '[Station WS Bridge] startMusicGeneration error:',
              error,
            );
          },
        );
      }
      break;

    case 'startImageGeneration':
      if (payload) {
        startImageGeneration(payload as StartImageGenerationParams).catch(
          (error) => {
            console.error(
              '[Station WS Bridge] startImageGeneration error:',
              error,
            );
          },
        );
      }
      break;

    case 'startPresentationGeneration':
      if (payload) {
        startPresentationGeneration(
          payload as StartPresentationGenerationParams,
        ).catch((error) => {
          console.error(
            '[Station WS Bridge] startPresentationGeneration error:',
            error,
          );
        });
      }
      break;

    case 'startBusinessPlanGeneration':
      if (payload) {
        startBusinessPlanGeneration(
          payload as StartBusinessPlanGenerationParams,
        ).catch((error) => {
          console.error(
            '[Station WS Bridge] startBusinessPlanGeneration error:',
            error,
          );
        });
      }
      break;

    case 'startWebpageGeneration':
      if (payload) {
        startWebpageGeneration(payload as StartWebpageGenerationParams).catch(
          (error) => {
            console.error(
              '[Station WS Bridge] startWebpageGeneration error:',
              error,
            );
          },
        );
      }
      break;

    case 'loadSpacesUseCase':
      if (payload) {
        startSpacesUseCase(payload as StartSpacesUseCaseParams).catch(
          (error) => {
            console.error(
              '[Station WS Bridge] loadSpacesUseCase error:',
              error,
            );
          },
        );
      }
      break;

    // Playback Bridge Messages
    case 'playTrackOrAlbum':
      if (payload) {
        playTrackOrAlbum(payload as PlayParams).catch((error) => {
          console.error('[Station WS Bridge] playTrackOrAlbum error:', error);
        });
      }
      break;

    case 'playCreation':
      if (payload) {
        playCreation(payload as PlayCreationParams).catch((error) => {
          console.error('[Station WS Bridge] playCreation error:', error);
        });
      }
      break;

    case 'duckAudio':
      if (payload && typeof payload.shouldDuck === 'boolean') {
        duckAudio(payload.shouldDuck);
      }
      break;

    // Companion Bridge Messages
    case 'companionMessage':
      if (payload && payload.message) {
        sendCompanionMessage(payload.message);
      }
      break;

    case 'startOver':
      sendStartOverMessage();
      break;

    case 'startHost':
      sendStartHostMessage();
      break;

    case 'selectCompanion':
      if (payload && payload.companionId) {
        sendSelectCompanionMessage(payload.companionId);
      }
      break;

    case 'speak':
      if (payload) {
        sendSpeakMessage(payload);
      }
      break;

    case 'stopTalking':
      sendStopTalkingMessage();
      break;

    case 'searchResults':
      if (payload) {
        sendSearchResultsMessage(payload);
      }
      break;

    case 'print':
      if (payload && payload.base64DataUrl) {
        sendPrintMessage(payload.base64DataUrl);
      }
      break;

    case 'volume':
      if (payload && typeof payload.volume === 'number') {
        updateNativeCompanionVolume(payload.volume);
      }
      break;

    case 'mute':
      if (payload && typeof payload.muted === 'boolean') {
        muteNativeCompanion(payload.muted);
      }
      break;

    // Regeneration Bridge Messages
    case 'regenerateLyrics':
      if (payload) {
        regenerateLyrics(payload as RegenerateLyricsParams).catch((error) => {
          console.error('[Station WS Bridge] regenerateLyrics error:', error);
        });
      }
      break;

    case 'regenerateCardImage':
      if (payload) {
        regenerateCardImage(payload as RegenerateCardImageParams).catch(
          (error) => {
            console.error(
              '[Station WS Bridge] regenerateCardImage error:',
              error,
            );
          },
        );
      }
      break;

    case 'regenerateImage':
      if (payload) {
        regenerateImage(payload as RegenerateImageParams).catch((error) => {
          console.error('[Station WS Bridge] regenerateImage error:', error);
        });
      }
      break;

    case 'regeneratePresentation':
      if (payload) {
        regeneratePresentation(payload as RegeneratePresentationParams).catch(
          (error) => {
            console.error(
              '[Station WS Bridge] regeneratePresentation error:',
              error,
            );
          },
        );
      }
      break;

    case 'regenerateBusinessPlan':
      if (payload) {
        regenerateBusinessPlan(payload as RegenerateBusinessPlanParams).catch(
          (error) => {
            console.error(
              '[Station WS Bridge] regenerateBusinessPlan error:',
              error,
            );
          },
        );
      }
      break;

    case 'regenerateWebpage':
      if (payload) {
        regenerateWebpage(payload as RegenerateWebpageParams).catch((error) => {
          console.error('[Station WS Bridge] regenerateWebpage error:', error);
        });
      }
      break;

    // Search Bridge Messages
    case 'search':
      if (payload) {
        performSearch(payload as SearchParams).catch((error) => {
          console.error('[Station WS Bridge] performSearch error:', error);
        });
      }
      break;

    // UI Actions Bridge Messages
    case 'next':
      goToNext().catch((error) => {
        console.error('[Station WS Bridge] goToNext error:', error);
      });
      break;

    case 'share':
      shareGeneration().catch((error) => {
        console.error('[Station WS Bridge] shareGeneration error:', error);
      });
      break;

    case 'switchVariant':
      switchToNextVariant().catch((error) => {
        console.error('[Station WS Bridge] switchToNextVariant error:', error);
      });
      break;

    default:
      console.log('[Station WS Bridge] Unhandled message type:', type);
  }
};

/**
 * Send a message via WebSocket if enabled
 */
const sendWebSocketMessage = (type: string, payload?: any) => {
  console.log('[Station WS Bridge] Attempting to send:', type);
  console.log('[Station WS Bridge] shouldUseWebSocket:', shouldUseWebSocket());
  console.log(
    '[Station WS Bridge] isConnected:',
    stationWebSocketClient.isConnected(),
  );

  if (!shouldUseWebSocket()) {
    console.warn(
      '[Station WS Bridge] WebSocket not enabled (check URL params and station mode)',
    );
    return;
  }

  if (!stationWebSocketClient.isConnected()) {
    console.warn('[Station WS Bridge] Not connected, cannot send message');
    return;
  }

  console.log('[Station WS Bridge] ✓ Sending to WebSocket:', type, payload);
  stationWebSocketClient.sendMessage(type, payload);
};

/**
 * Wrapper functions that intercept bridge calls and send to WebSocket
 * These should be called instead of the original bridge functions when WebSocket is enabled
 */

export const wsStartMusicGeneration = async (
  params: StartMusicGenerationParams,
) => {
  sendWebSocketMessage('startMusicGeneration', params);
  return startMusicGeneration(params);
};

export const wsStartImageGeneration = async (
  params: StartImageGenerationParams,
) => {
  sendWebSocketMessage('startImageGeneration', params);
  return startImageGeneration(params);
};

export const wsStartPresentationGeneration = async (
  params: StartPresentationGenerationParams,
) => {
  sendWebSocketMessage('startPresentationGeneration', params);
  return startPresentationGeneration(params);
};

export const wsStartBusinessPlanGeneration = async (
  params: StartBusinessPlanGenerationParams,
) => {
  sendWebSocketMessage('startBusinessPlanGeneration', params);
  return startBusinessPlanGeneration(params);
};

export const wsStartWebpageGeneration = async (
  params: StartWebpageGenerationParams,
) => {
  sendWebSocketMessage('startWebpageGeneration', params);
  return startWebpageGeneration(params);
};

export const wsStartSpacesUseCase = async (
  params: StartSpacesUseCaseParams,
) => {
  sendWebSocketMessage('loadSpacesUseCase', params);
  return startSpacesUseCase(params);
};

export const wsPlayTrackOrAlbum = async (params: PlayParams) => {
  sendWebSocketMessage('playTrackOrAlbum', params);
  return playTrackOrAlbum(params);
};

export const wsPlayCreation = async (params: PlayCreationParams) => {
  sendWebSocketMessage('playCreation', params);
  return playCreation(params);
};

export const wsDuckAudio = (shouldDuck: boolean) => {
  sendWebSocketMessage('duckAudio', { shouldDuck });
  duckAudio(shouldDuck);
};

// Companion Bridge Wrappers
export const wsSendCompanionMessage = (message: string): boolean => {
  if (shouldUseWebSocket()) {
    sendWebSocketMessage('companionMessage', { message });
    return true;
  }
  return sendCompanionMessage(message);
};

export const wsSendStartOverMessage = (): boolean => {
  if (shouldUseWebSocket()) {
    sendWebSocketMessage('startOver', {});
    return true;
  }
  return sendStartOverMessage();
};

export const wsSendStartHostMessage = (): boolean => {
  if (shouldUseWebSocket()) {
    sendWebSocketMessage('startHost', {});
    return true;
  }
  return sendStartHostMessage();
};

export const wsSendSelectCompanionMessage = (companionId: string): boolean => {
  if (shouldUseWebSocket()) {
    sendWebSocketMessage('selectCompanion', { companionId });
    return true;
  }
  return sendSelectCompanionMessage(companionId);
};

export const wsSendSpeakMessage = (data: unknown): boolean => {
  if (shouldUseWebSocket()) {
    sendWebSocketMessage('speak', data);
    return true;
  }
  return sendSpeakMessage(data);
};

export const wsSendStopTalkingMessage = (): boolean => {
  if (shouldUseWebSocket()) {
    sendWebSocketMessage('stopTalking', {});
    return true;
  }
  return sendStopTalkingMessage();
};

export const wsSendSearchResultsMessage = (data: unknown): boolean => {
  if (shouldUseWebSocket()) {
    sendWebSocketMessage('searchResults', data);
    return true;
  }
  return sendSearchResultsMessage(data);
};

export const wsSendPrintMessage = (base64DataUrl: string): boolean => {
  if (shouldUseWebSocket()) {
    sendWebSocketMessage('print', { base64DataUrl });
    return true;
  }
  return sendPrintMessage(base64DataUrl);
};

export const wsUpdateNativeCompanionVolume = (volume: number): boolean => {
  if (shouldUseWebSocket()) {
    sendWebSocketMessage('volume', { volume });
    return true;
  }
  return updateNativeCompanionVolume(volume);
};

export const wsMuteNativeCompanion = (muted: boolean): boolean => {
  if (shouldUseWebSocket()) {
    sendWebSocketMessage('mute', { muted });
    return true;
  }
  return muteNativeCompanion(muted);
};

// Regeneration Bridge Wrappers
export const wsRegenerateLyrics = async (params: RegenerateLyricsParams) => {
  if (shouldUseWebSocket()) {
    sendWebSocketMessage('regenerateLyrics', params);
  }
  return regenerateLyrics(params);
};

export const wsRegenerateCardImage = async (
  params: RegenerateCardImageParams,
) => {
  if (shouldUseWebSocket()) {
    sendWebSocketMessage('regenerateCardImage', params);
  }
  return regenerateCardImage(params);
};

export const wsRegenerateImage = async (params: RegenerateImageParams) => {
  if (shouldUseWebSocket()) {
    sendWebSocketMessage('regenerateImage', params);
  }
  return regenerateImage(params);
};

export const wsRegeneratePresentation = async (
  params: RegeneratePresentationParams,
) => {
  if (shouldUseWebSocket()) {
    sendWebSocketMessage('regeneratePresentation', params);
  }
  return regeneratePresentation(params);
};

export const wsRegenerateBusinessPlan = async (
  params: RegenerateBusinessPlanParams,
) => {
  if (shouldUseWebSocket()) {
    sendWebSocketMessage('regenerateBusinessPlan', params);
  }
  return regenerateBusinessPlan(params);
};

export const wsRegenerateWebpage = async (params: RegenerateWebpageParams) => {
  if (shouldUseWebSocket()) {
    sendWebSocketMessage('regenerateWebpage', params);
  }
  return regenerateWebpage(params);
};

// Search Bridge Wrappers
export const wsPerformSearch = async (params: SearchParams) => {
  if (shouldUseWebSocket()) {
    sendWebSocketMessage('search', params);
  }
  return performSearch(params);
};

// UI Actions Bridge Wrappers
export const wsGoToNext = async () => {
  if (shouldUseWebSocket()) {
    sendWebSocketMessage('next', {});
  }
  return goToNext();
};

export const wsShareGeneration = async () => {
  if (shouldUseWebSocket()) {
    sendWebSocketMessage('share', {});
  }
  return shareGeneration();
};

export const wsSwitchToNextVariant = async () => {
  if (shouldUseWebSocket()) {
    sendWebSocketMessage('switchVariant', {});
  }
  return switchToNextVariant();
};
